import { useEffect, useMemo, useState } from 'react'

import { Grid } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

import { ConfirmDistanceChangeDialog } from 'components/bookings/common'
import { ControlledTextField } from 'components/common'
import {
  GetBookingByTrackingHashQuery,
  GetBookingQuery,
  GetUserBookingQuery,
} from 'generated/graphql'
import { calcBookingConsignmentFee } from 'helpers'
import { Restricted, usePermission } from 'providers'

type BookingDetails = NonNullable<
  NonNullable<GetBookingByTrackingHashQuery['bookingByTrackingHash']>['booking']
>

interface IProps {
  bookingDetails: GetBookingQuery['booking'] | GetUserBookingQuery['userBooking'] | BookingDetails
}

const DistanceAndConsignmentFee = (props: IProps) => {
  const { bookingDetails } = props
  const [openConfirmDistanceChangeDialog, setOpenConfirmDistanceChangeDialog] = useState(false)

  const {
    control,
    getValues,
    setValue,
    formState: { dirtyFields },
  } = useFormContext()

  const distance = useWatch({ name: 'distance' })
  const isWaitAndReturn = useWatch({ name: 'isWaitAndReturn' })
  const returnDistance = useWatch({ name: 'returnDistance' })

  const [isAllowedToShowConfirmationDialogWhenDockedIsRePriced] = usePermission(
    'ShowConfirmationDialogWhenDockedIsRePriced',
  )

  const calculatedConsignmentFeeFromTariff = useMemo(() => {
    if (!bookingDetails?.tariff || !distance) {
      return 0
    }

    if (isWaitAndReturn && !returnDistance) {
      return 0
    }

    const result = calcBookingConsignmentFee(
      distance - returnDistance,
      bookingDetails.tariff.minMiles,
      bookingDetails.tariff.minCharge,
      bookingDetails.tariff.rate,
    )

    return result
  }, [distance, returnDistance, isWaitAndReturn, bookingDetails?.tariff])

  useEffect(() => {
    const currentConsignmentFeeValue = getValues('consignmentFee') ?? 0

    if (calculatedConsignmentFeeFromTariff === currentConsignmentFeeValue) {
      return
    }

    const isDistanceDirty = !!dirtyFields.distance
    const isReturnDistanceDirty = !!dirtyFields.returnDistance

    const shouldHandleDistanceChange =
      (isDistanceDirty || isReturnDistanceDirty) && (!isWaitAndReturn || !!returnDistance)

    if (!shouldHandleDistanceChange) {
      return
    }

    if (isAllowedToShowConfirmationDialogWhenDockedIsRePriced) {
      setOpenConfirmDistanceChangeDialog(true)
    } else {
      setValue('consignmentFee', calculatedConsignmentFeeFromTariff, { shouldDirty: true })
    }
  }, [
    calculatedConsignmentFeeFromTariff,
    getValues,
    isAllowedToShowConfirmationDialogWhenDockedIsRePriced,
    isWaitAndReturn,
    returnDistance,
    setValue,
    dirtyFields.distance,
    dirtyFields.returnDistance,
  ])

  const handleConfirmChangeDistance = () => {
    if (calculatedConsignmentFeeFromTariff) {
      setValue('consignmentFee', calculatedConsignmentFeeFromTariff)
    }
  }

  return (
    <>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ControlledTextField
            control={control}
            name='distance'
            label='Distance'
            readOnly
            endAdornment='mi'
          />
        </Grid>
        <Restricted to='ViewBookingFeeAndChargesDetails'>
          <Grid item xs={12} md={6}>
            <ControlledTextField
              control={control}
              name='consignmentFee'
              label='Consignment Fee'
              readOnly
            />
          </Grid>
        </Restricted>
      </Grid>

      <ConfirmDistanceChangeDialog
        open={openConfirmDistanceChangeDialog}
        setOpen={setOpenConfirmDistanceChangeDialog}
        onConfirm={handleConfirmChangeDistance}
      />
    </>
  )
}

export default DistanceAndConsignmentFee
